import Big from "big.js";
import { GST_REGISTRATION_STATUS_OPTIONS, INVOICE_BACKDATING_STRATEGY, INVOICE_DISPLAY_MODE, INVOICE_TYPE, PAYMENT_STATUSES, systemDefaultValues, TAX_MODES, } from "../constants/index.js";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat.js";
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";
dayjs.extend(localizedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
/**
 * Calculates the tax amount for a given price, tax rate, and tax mode.
 * @param price - The price of the item.
 * @param taxRate - The tax rate percentage (e.g., 10 for 10%).
 * @param taxMode - The tax mode ('INCLUSIVE', 'EXCLUSIVE', 'NO_TAX').
 * @returns The calculated tax amount as a Big.js object.
 */
export const getTaxAmount = (price, taxRate, taxMode) => {
    const basePrice = new Big(price ?? 0);
    if (!taxRate || taxMode === TAX_MODES.NO_TAX.id) {
        return new Big(0);
    }
    const taxRateBig = typeof taxRate === "number" ? new Big(taxRate) : new Big(taxRate.total);
    const rate = new Big(taxRateBig).div(100);
    if (taxMode === TAX_MODES.INCLUSIVE.id) {
        // For inclusive tax: tax = price - (price / (1 + rate))
        return basePrice.minus(basePrice.div(new Big(1).plus(rate))).round(2);
    }
    // For exclusive tax: tax = price * rate
    return basePrice.times(rate).round(2);
};
/**
 * Calculates the price before tax for a given price, tax rate, and tax mode.
 * @param price - The price of the item.
 * @param taxRate - The tax rate percentage (e.g., 10 for 10%).
 * @param taxMode - The tax mode ('INCLUSIVE', 'EXCLUSIVE', 'NO_TAX').
 * @returns The price before tax as a Big.js object.
 */
export const getPriceBeforeTax = (price, taxRate, taxMode) => {
    const basePrice = new Big(price ?? 0);
    if (basePrice.eq(0) || !taxRate || taxMode !== TAX_MODES.INCLUSIVE.id) {
        // Return base price if:
        // - Price is 0 OR
        // - No tax rate is specified OR
        // - Tax mode is not inclusive
        return basePrice;
    }
    const taxRateBig = typeof taxRate === "number" ? new Big(taxRate) : new Big(taxRate.total);
    // For inclusive tax: priceBeforeTax = price / (1 + rate)
    const rate = new Big(taxRateBig).div(100);
    return basePrice.div(new Big(1).plus(rate)).round(2);
};
export const toFiniteNumber = (value, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
};
export const getApportionedAmount = (amount, apportionmentRate) => {
    const baseAmount = new Big(amount ?? 0);
    const rate = new Big(apportionmentRate ?? 0).div(100);
    return baseAmount.times(rate).round(2);
};
export const getInvoiceSectionItemsTotal = (invoiceSectionItems, invoiceType = INVOICE_TYPE.STANDARD) => {
    // item subtotal
    let subtotal = new Big(0);
    // total per tax rate
    const taxTotals = {};
    // total after tax
    let total = new Big(0);
    /**
     * Prices in this section were already calculated as base prices in the backend.
     */
    invoiceSectionItems.forEach((item) => {
        const { quantity, price, taxRate, taxMode, taxName, apportionmentRate } = item;
        const itemPrice = new Big(toFiniteNumber(price));
        const itemQuantity = new Big(toFiniteNumber(quantity, 1));
        const lineTotal = itemPrice.times(itemQuantity);
        const isRcti = invoiceType === INVOICE_TYPE.RCTI;
        if (taxRate && taxMode !== TAX_MODES.NO_TAX.id) {
            // 1. First get the standard base price before tax (respects taxMode)
            let priceBeforeTax = getPriceBeforeTax(lineTotal, taxRate, taxMode);
            let taxAmount = getTaxAmount(lineTotal, taxRate, taxMode);
            // 2. Apportion both subtotal and tax amounts if RCTI
            if (isRcti) {
                priceBeforeTax = getApportionedAmount(priceBeforeTax, apportionmentRate);
                taxAmount = getApportionedAmount(taxAmount, apportionmentRate);
            }
            subtotal = subtotal.plus(priceBeforeTax);
            const taxKey = taxName || "Tax";
            if (taxTotals[taxKey]) {
                taxTotals[taxKey].total = new Big(taxTotals[taxKey].total)
                    .plus(taxAmount)
                    .toNumber();
            }
            else {
                taxTotals[taxKey] = {
                    total: taxAmount.toNumber(),
                };
            }
        }
        else {
            // no tax is applied
            const lineSubtotal = isRcti
                ? getApportionedAmount(lineTotal, apportionmentRate)
                : lineTotal;
            subtotal = subtotal.plus(lineSubtotal);
        }
    });
    // finalize total by adding the subtotal of items and all the totals per tax rate
    total = total.plus(subtotal);
    Object.values(taxTotals).forEach((taxTotal) => {
        total = total.plus(new Big(taxTotal.total));
    });
    return { subtotal: subtotal.toNumber(), taxTotals, total: total.toNumber() };
};
export const formatMoneyAmount = (value, options = {}) => {
    let numericValue;
    try {
        numericValue = new Big(value ?? 0).toNumber();
    }
    catch {
        numericValue = 0;
    }
    return new Intl.NumberFormat(systemDefaultValues.locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        ...options,
    }).format(numericValue);
};
export const formatWithCurrency = (value, currency) => {
    const upperCode = currency.toUpperCase();
    return `${upperCode} ${formatMoneyAmount(value)}`;
};
const getIssueDate = (invoice) => {
    // Use localized issue date as priority
    if (invoice?.issueDateLocal) {
        return dayjs(invoice.issueDateLocal.slice(0, 19)).format("LL");
    }
    // Fallback to issue date
    if (invoice?.issueDate) {
        return dayjs(invoice.issueDate).format("LL");
    }
    return "-";
};
const getDueDate = (invoice) => {
    // Use localized due date as priority
    if (invoice?.dueDateLocal) {
        return dayjs(invoice.dueDateLocal.slice(0, 19)).format("LL");
    }
    // Fallback to due date
    if (invoice?.dueDate) {
        return dayjs(invoice.dueDate).format("LL");
    }
    return "-";
};
// --- Bill To ---
export const getBillToName = (invoice) => {
    if (invoice?.billToName) {
        return invoice.billToName;
    }
    else if (invoice?.BillTo) {
        const companyName = invoice?.Company?.name;
        const billToName = `${invoice.BillTo.firstName ?? ""} ${invoice.BillTo.lastName ?? ""}`.trim();
        return companyName ? `${companyName} | ${billToName}` : billToName;
    }
    else if (invoice?.Company) {
        return invoice.Company.name ?? "";
    }
    return "";
};
export const getBillToBusinessNumber = (invoice) => {
    if (invoice?.billToBusinessNumber) {
        return invoice.billToBusinessNumber;
    }
    else if (invoice?.Company) {
        return invoice.Company.businessNumber ?? "";
    }
    return "";
};
export const getBillToAddressLine1 = (invoice) => {
    if (invoice?.billToAddressLine1) {
        return invoice.billToAddressLine1;
    }
    else if (invoice?.BillTo) {
        return invoice.BillTo.addressLine1 ?? "";
    }
    else if (invoice?.Company) {
        return invoice.Company.address ?? "";
    }
    return "";
};
export const getBillToAddressLine2 = (invoice) => {
    if (invoice?.billToAddressLine2) {
        return invoice.billToAddressLine2;
    }
    else if (invoice?.BillTo) {
        return invoice.BillTo.addressLine2 ?? "";
    }
    return "";
};
export const getPaymentTerms = (invoice) => {
    if (invoice?.Company) {
        return invoice.Company.paymentTerms ?? "";
    }
    return "";
};
// --- RCTI Supplier (Manager) Details ---
const getSupplierAddress = (invoice) => {
    const manager = invoice?.Manager;
    if (!manager) {
        return "";
    }
    return [manager.suburb, manager.state, manager.postCode]
        .filter(Boolean)
        .join(" ");
};
const getSupplierGstRegistrationStatusLabel = (invoice) => {
    const status = invoice?.Manager?.gstRegistrationStatus;
    if (!status) {
        return "";
    }
    return (GST_REGISTRATION_STATUS_OPTIONS[status]?.label ?? "");
};
/**
 * Takes raw invoice data and prepares it for rendering in the PDF document.
 * This contains all the logic previously in the component's useMemo hooks.
 */
export const prepareInvoicePdfData = ({ invoice, bankDetails, pdfHeaderImgUrl, invoiceDisplayMode = INVOICE_DISPLAY_MODE.DETAILED, }) => {
    let invoiceSections = invoice.InvoiceSections ?? [];
    // --- Totals Logic ---
    let allInvoiceSectionItems = invoiceSections.flatMap((s) => s.InvoiceSectionItems ?? []) ?? [];
    // --- Consolidated Mode Logic ---
    if (invoiceDisplayMode === INVOICE_DISPLAY_MODE.CONSOLIDATED) {
        const groupedItems = new Map();
        allInvoiceSectionItems.forEach((item) => {
            // Create a unique key based on the grouping criteria
            // This groups the items by item code, price and tax details
            const key = `${item.itemCode}-${item.price}-${item.taxRate}-${item.taxMode}-${item.taxName}`;
            if (groupedItems.has(key)) {
                const existing = groupedItems.get(key);
                // Sum the quantities (and any other additive fields like total amount if applicable)
                existing.quantity = new Big(existing.quantity || 0)
                    .add(new Big(item.quantity || 0))
                    .toNumber();
                existing.amount = new Big(existing.amount || 0)
                    .add(new Big(item.amount || 0))
                    .toNumber();
            }
            else {
                // Clone the item to avoid mutating the original invoice object
                groupedItems.set(key, {
                    ...item,
                    description: `Consolidated totals for ${item.itemCode}`,
                });
            }
        });
        allInvoiceSectionItems = Array.from(groupedItems.values());
        // In consolidated mode, we override Invoice Sections with a single section containing grouped items
        invoiceSections = [
            {
                id: `invoice-${invoice.id}-consolidated-section`,
                name: "Consolidated Section",
                respondentId: null,
                InvoiceSectionItems: allInvoiceSectionItems,
            },
        ];
    }
    const { subtotal, taxTotals } = getInvoiceSectionItemsTotal(allInvoiceSectionItems, invoice.type);
    // --- Payments Logic ---
    const paidPayments = invoice.Payments?.filter((p) => p.status === PAYMENT_STATUSES.PAID.id ||
        p.status === PAYMENT_STATUSES.REFUNDED.id) ?? [];
    const { grossAmount } = paidPayments.reduce((acc, p) => ({
        grossAmount: acc.grossAmount + p.grossAmount,
    }), { grossAmount: 0 });
    const isRcti = invoice.type === INVOICE_TYPE.RCTI;
    // --- Combine everything ---
    return {
        ...invoice,
        InvoiceSections: invoiceSections,
        subtotal,
        taxTotals,
        grossAmount,
        issueDate: getIssueDate(invoice),
        dueDate: getDueDate(invoice),
        invoiceNumber: invoice?.invoiceNumber ?? "-",
        reference: invoice?.reference ?? "-",
        bankAccountName: isRcti ? "" : (bankDetails?.bankAccountName ?? ""),
        bankAccountNumber: isRcti ? "" : (bankDetails?.bankAccountNumber ?? ""),
        bankAccountBSB: isRcti ? "" : (bankDetails?.bankAccountBSB ?? ""),
        companyName: isRcti
            ? (invoice.Manager?.legalEntity ?? invoice.Manager?.displayName ?? "")
            : (bankDetails?.companyName ?? ""),
        australianBusinessNumber: isRcti
            ? (invoice.Manager?.abn ?? "")
            : (bankDetails?.australianBusinessNumber ?? ""),
        companyAddress1: isRcti
            ? (invoice.Manager?.address ?? "")
            : (bankDetails?.companyAddress1 ?? ""),
        companyAddress2: isRcti
            ? getSupplierAddress(invoice)
            : (bankDetails?.companyAddress2 ?? ""),
        gstRegistrationStatus: isRcti
            ? getSupplierGstRegistrationStatusLabel(invoice)
            : "",
        supplierBankName: isRcti ? (invoice.Manager?.bankName ?? "") : "",
        supplierAccountNumber: isRcti ? (invoice.Manager?.accountNumber ?? "") : "",
        supplierAccountName: isRcti ? (invoice.Manager?.accountName ?? "") : "",
        referenceNumberHelpText: bankDetails?.referenceNumberHelpText ?? "",
        remittanceHelpText: bankDetails?.remittanceHelpText ?? "",
        queriesHelpText: bankDetails?.queriesHelpText ?? "",
        phone: bankDetails?.phone ?? "",
        website: bankDetails?.website ?? "",
        companyLogo: pdfHeaderImgUrl ||
            "https://stsurveyatlasdev001.blob.core.windows.net/azure-storage-container-blob/logo-images/f161fe1b-7518-4905-8224-cbb798ab0029-tenantlogo",
        billToName: getBillToName(invoice),
        billToBusinessNumber: getBillToBusinessNumber(invoice),
        billToAddressLine1: getBillToAddressLine1(invoice),
        billToAddressLine2: getBillToAddressLine2(invoice),
        paymentTerms: getPaymentTerms(invoice),
        billerCompanyName: isRcti ? (bankDetails?.companyName ?? "") : "",
        billerCompanyAddress1: isRcti ? (bankDetails?.companyAddress1 ?? "") : "",
        billerCompanyAddress2: isRcti ? (bankDetails?.companyAddress2 ?? "") : "",
        billerABN: isRcti ? (bankDetails?.australianBusinessNumber ?? "") : "",
    };
};
/**
 * Computes the backdated invoice date using Day.js.
 * @param originalDate The date from which the backdating will start.
 * @param strategy The backdating strategy to be used.
 * @param tz The time zone to be applied to `originalDate`. Defaults to system time zone if none was provided.
 */
export const computeInvoiceBackdate = (originalDate, strategy, tz = systemDefaultValues.timezone) => {
    // 1. Initialize the date in the specific timezone
    const date = dayjs(originalDate).tz(tz);
    // 2. Move to the last day of the previous month
    // .date(0) in Day.js is a shortcut to the last day of the previous month
    let target = date.date(0).startOf("day");
    switch (strategy) {
        case INVOICE_BACKDATING_STRATEGY.LAST_DAY:
            return target;
        case INVOICE_BACKDATING_STRATEGY.LAST_BUSINESS_DAY:
            // 0 = Sunday, 6 = Saturday
            while (target.day() === 0 || target.day() === 6) {
                target = target.subtract(1, "day");
            }
            return target;
        default: {
            const dayMap = {
                [INVOICE_BACKDATING_STRATEGY.LAST_SUNDAY]: 0,
                [INVOICE_BACKDATING_STRATEGY.LAST_MONDAY]: 1,
                [INVOICE_BACKDATING_STRATEGY.LAST_TUESDAY]: 2,
                [INVOICE_BACKDATING_STRATEGY.LAST_WEDNESDAY]: 3,
                [INVOICE_BACKDATING_STRATEGY.LAST_THURSDAY]: 4,
                [INVOICE_BACKDATING_STRATEGY.LAST_FRIDAY]: 5,
                [INVOICE_BACKDATING_STRATEGY.LAST_SATURDAY]: 6,
            };
            const targetDay = dayMap[strategy];
            // Return early if target day is undefined
            if (typeof targetDay !== "number") {
                return target;
            }
            // Step back until we hit the correct day of the week
            while (target.day() !== targetDay) {
                target = target.subtract(1, "day");
            }
            return target;
        }
    }
};
//# sourceMappingURL=invoice.js.map